import React from "react";
import { RouteMap } from "./RouteMap";
import { Clock, Car, Receipt, Wallet, Route as RouteIcon } from "lucide-react";

const fmt = (n, sym) => `${Number(n).toFixed(2)} ${sym}`;

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-slate-500 mb-2">
      <Icon size={16} strokeWidth={2} />
      <span className="text-sm font-medium">{label}</span>
    </div>
    <div className="text-2xl font-bold tracking-tight text-slate-900">{value}</div>
    {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
  </div>
);

export const ResultsSummary = ({ result }) => {
  const { calculation: c, route } = result;
  const sym = c.currency_symbol;

  return (
    <div className="mt-8 space-y-6" data-testid="results-summary">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Clock}
          label="Trajanje"
          value={`${c.total_hours} h`}
          sub={`${c.per_diem.full_count} punih + ${c.per_diem.half_count} pola dnevnica`}
          testid="stat-duration"
        />
        <StatCard
          icon={RouteIcon}
          label="Udaljenost"
          value={`${c.transport.effective_km} km`}
          sub={c.transport.round_trip ? "povratno" : "jednosmjerno"}
          testid="stat-distance"
        />
        <StatCard
          icon={Wallet}
          label="Dnevnice ukupno"
          value={fmt(c.per_diem.total, sym)}
          sub={`${fmt(c.per_diem.per_person, sym)} po osobi × ${c.per_diem.num_teachers}`}
          testid="stat-perdiem"
        />
        <StatCard
          icon={Car}
          label="Prijevoz"
          value={fmt(c.transport.total, sym)}
          sub={c.transport.mode_label}
          testid="stat-transport"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Detaljan obračun */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-medium tracking-tight text-slate-900 mb-4">
            Detaljan obračun
          </h3>
          <dl className="divide-y divide-slate-100">
            <Line
              label={`Dnevnice (${c.per_diem.full_count}×${fmt(c.per_diem.full_rate, sym)} + ${c.per_diem.half_count}×${fmt(c.per_diem.half_rate, sym)})`}
              value={fmt(c.per_diem.total, sym)}
            />
            <Line label={`Prijevoz — ${c.transport.detail}`} value={fmt(c.transport.total, sym)} />
            <Line label="Cestarine" value={fmt(c.tolls, sym)} />
            <Line label="Ostali troškovi" value={fmt(c.other_costs, sym)} />
          </dl>

          <div
            className="mt-5 rounded-xl bg-blue-50 border border-blue-100 p-5 flex items-center justify-between"
            data-testid="grand-total"
          >
            <div className="flex items-center gap-2 text-blue-900">
              <Receipt size={20} />
              <span className="font-semibold">UKUPNO</span>
            </div>
            <span className="text-3xl font-bold tracking-tight text-blue-700">
              {fmt(c.grand_total, sym)}
            </span>
          </div>
        </div>

        {/* Karta */}
        <div>
          <h3 className="text-xl font-medium tracking-tight text-slate-900 mb-4">Ruta na karti</h3>
          {route ? (
            <RouteMap route={route} />
          ) : (
            <div className="w-full min-h-[300px] h-[360px] rounded-xl border border-dashed border-slate-300 grid place-items-center text-slate-400 text-sm">
              Karta nije dostupna (korištena je ručno unesena udaljenost).
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Line = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 gap-4">
    <dt className="text-sm text-slate-600">{label}</dt>
    <dd className="text-sm font-semibold text-slate-900 whitespace-nowrap">{value}</dd>
  </div>
);
